/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react";
import {
	Container,
	Grid,
	Box,
	Typography,
	CircularProgress,
} from "@mui/material";
import { useAppTheme } from "../../utils/theme";
import { useUserStore } from "../../stores/user.store";
import { EventsAndAlertsComponent } from "../../pages/Dashboard/EventsAndAlertsComponent";
import Header from "../../pages/Dashboard/Header";
import { useGetEvents } from "../../hooks/useEvents";
import { useGetTreatmentPlans } from "../../hooks/useTreatmentPlans";
import { useGetProgramsByIds } from "../../hooks/useBeneficiaries";
import { EventKPI } from "../../domain/event.interface";

const { QlikEmbed } = require("@qlik/embed-react"); // eslint-disable-line @typescript-eslint/no-var-requires

interface Filters {
	programId: string;
	providerId: string;
	providerSiteId: string;
	search: string;
	pageNumber: number;
	pageSize: number;
	dateRange: "lastMonth" | "lastQuarter" | "lastYear";
	zipCode: string;
}

interface Card {
	cardId: string;
	action: string;
}

interface Section {
	title?: string;
	action: string;
	cards: Card[];
}

interface KPI extends EventKPI {
	_id: string;
	variables: Array<{ name: string; value: string }>;
}

interface MultiTreatmentDashboardProps {
	filters: Filters;
	setFilters: React.Dispatch<React.SetStateAction<Filters>>;
	applyFilters: () => void;
	actionsAndCards: Section[];
	qlikApi: any;
	setQlikApi: (api: any) => void;
}

const MultiTreatmentDashboard: React.FC<MultiTreatmentDashboardProps> = ({
	filters,
	setFilters,
	applyFilters,
	actionsAndCards,
	setQlikApi,
}) => {
	const theme = useAppTheme();
	const { userInfo, userHasAccessToCode } = useUserStore();

	const APP_ID = process.env.REACT_APP_QLIK_APP_ID || "";

	// Store KPI API references for reapplying filters
	const kpiApisRef = useRef<Map<string, any>>(new Map());

	// Get program data to find treatment plan
	const { data: programs } = useGetProgramsByIds(
		filters.programId ? [filters.programId] : [],
	);

	// Get treatment plans
	const { data: treatmentPlans } = useGetTreatmentPlans();

	// Get events for KPI extraction
	const { data: events } = useGetEvents();

	// Reapply filters to all KPI cards when filters change
	useEffect(() => {
		if (kpiApisRef.current.size === 0) return;

		kpiApisRef.current.forEach((apiData) => {
			if (!apiData.api) return;

			apiData.api.getDoc().then(({ app }: any) => {
				// Apply general filters (same as Dashboard)
				app.clearAll();

				// Set date range
				const now = new Date();
				const start = new Date(now);
				const daysBack =
					filters.dateRange === "lastYear"
						? 365
						: filters.dateRange === "lastQuarter"
							? 90
							: 30;
				start.setDate(now.getDate() - daysBack);

				const startDate = start.toISOString().slice(0, 10);
				const today = now.toISOString().slice(0, 10);

				app
					.getVariableByName("vStartDate")
					.then((v: any) => v.setStringValue(startDate))
					.catch((e: Error) => {
						console.log(e);
					});

				app
					.getVariableByName("vEndDate")
					.then((v: any) => v.setStringValue(today))
					.catch((e: Error) => {
						console.log(e);
					});

				// Set program filter
				if (filters.programId) {
					app
						.getVariableByName("vSelectedProgram")
						.then((v: any) => v.setStringValue(filters.programId))
						.catch((e: Error) => {
							console.log(e);
						});
				}

				// Set provider site filter
				if (filters.providerSiteId) {
					app
						.getVariableByName("vSelectedOU")
						.then((v: any) =>
							v.setStringValue(filters.providerSiteId.replaceAll(",", "|")),
						)
						.catch((e: Error) => {
							console.log(e);
						});
				}

				// Set provider filter
				if (filters.providerId) {
					app
						.getVariableByName("vSelectedProvider")
						.then((v: any) => v.setStringValue(filters.providerId))
						.catch((e: Error) => {
							console.log(e);
						});
				}

				// Apply zip code filter if present
				if (filters.zipCode) {
					app
						.getField("organizations.address_zipCode")
						.then((f: any) =>
							f.selectValues([{ qText: filters.zipCode }], false, false),
						)
						.catch((e: Error) => {
							console.log(e);
						});
				}

				// Set KPI-specific variables
				if (apiData.variables && apiData.variables.length > 0) {
					apiData.variables.forEach(
						(variable: { name: string; value: string }) => {
							app
								.getVariableByName(variable.name)
								.then((v: any) => v.setStringValue(variable.value))
								.catch((e: Error) => {
									console.log(e);
								});
						},
					);
				}
			});
		});
	}, [filters]);

	// Extract events with their active KPIs grouped by event (filtered by treatment plan behaviors)
	const eventsWithKPIs = React.useMemo(() => {
		if (!filters.programId || !events || !programs || !treatmentPlans) {
			return [];
		}

		// Get the current program
		const currentProgram = programs.find((p) => p._id === filters.programId);
		if (!currentProgram?.treatmentPlan) {
			return [];
		}

		// Get the treatment plan associated with this program
		const treatmentPlan = treatmentPlans.find(
			(tp) => tp._id === currentProgram.treatmentPlan,
		);
		if (!treatmentPlan?.treatmentConfigOptions?.behavior) {
			return [];
		}

		// Get all behavior IDs from the treatment plan
		const behaviorIds = treatmentPlan.treatmentConfigOptions.behavior;

		// Filter events to only include those that are in the treatment plan behaviors
		const filteredEvents = events.filter((event) =>
			behaviorIds.includes(event._id),
		);

		// Group KPIs by event and filter only events with active KPIs
		const eventsWithActiveKPIs = filteredEvents
			.map((event) => {
				// Check if event has sectionConfig and user has access
				if (
					event.sectionConfig?.action &&
					!userHasAccessToCode(event.sectionConfig.action)
				) {
					return null;
				}

				if (!event.dashboardKPIs || !Array.isArray(event.dashboardKPIs)) {
					return null;
				}

				const activeKPIs = event.dashboardKPIs
					.map((kpi, kpiIndex) => ({
						_id: `${event._id}-kpi-${kpiIndex}`,
						name: kpi.name,
						url: kpi.url,
						active: kpi.active || false,
						position: kpi.position || 0,
						variables: kpi.variables || [], // Include variables
					}))
					.filter((kpi) => kpi.active)
					.sort((a, b) => a.position - b.position); // Sort KPIs within each event by position

				return activeKPIs.length > 0
					? {
							eventId: event._id,
							eventName: event.name,
							sectionTitle: event.sectionConfig?.title || event.name,
							sectionPosition: event.sectionConfig?.position || 0,
							sectionAction: event.sectionConfig?.action,
							kpis: activeKPIs,
						}
					: null;
			})
			.filter(Boolean) // Remove events with no active KPIs or no access
			.sort((a, b) => (a?.sectionPosition || 0) - (b?.sectionPosition || 0)); // Sort by section position

		return eventsWithActiveKPIs;
	}, [
		filters.programId,
		events,
		programs,
		treatmentPlans,
		userHasAccessToCode,
	]);

	// Helper component for loading container (copied from Dashboard)
	const LoaderContainer = ({
		height,
		children,
	}: {
		height: number;
		children: React.ReactNode;
	}) => {
		return (
			<Box
				sx={{
					position: "relative",
					width: "100%",
					height,
					bgcolor: "background.paper",
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 1,
						pointerEvents: "none",
					}}
				>
					<CircularProgress />
				</Box>
				<Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
					{children}
				</Box>
			</Box>
		);
	};

	// Get the first section (overview) and second section (events & alerts)
	const overviewSection = actionsAndCards[0];
	const eventsSection = actionsAndCards[1];

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{userHasAccessToCode("DASH_VIEW") && (
				<>
					<Header
						filtersState={[filters, setFilters]}
						handleSearch={applyFilters}
					/>
					{/* Section 0: Overview Cards */}
					{overviewSection && userHasAccessToCode(overviewSection.action) && (
						<>
							{overviewSection.title && (
								<Typography variant="headlineSmall" sx={{ mb: 2 }}>
									{overviewSection.title}
								</Typography>
							)}
							<Grid container spacing={2} sx={{ mb: 4 }}>
								{overviewSection.cards.map((card: Card) =>
									card.cardId && userHasAccessToCode(card.action) ? (
										<Grid item key={card.cardId} xs={12} sm={6} md={3}>
											{LoaderContainer({
												height: 196,
												children: (
													<Box sx={{ height: 196 }}>
														<QlikEmbed
															ui="analytics/chart"
															appId={APP_ID}
															objectId={card.cardId}
															identity={userInfo?.id}
															style={{ width: "100%", height: "100%" }}
														/>
													</Box>
												),
											})}
										</Grid>
									) : null,
								)}
							</Grid>
						</>
					)}
					{eventsSection && userHasAccessToCode(eventsSection.action) && (
						<Grid container spacing={2} sx={{ mb: 4 }}>
							<Grid item xs={12} md={7}>
								<Box
									sx={{
										height: 364,
										bgcolor: "background.paper",
										borderRadius: 2,
										boxShadow: 1,
										overflowY: "auto",
									}}
								>
									<EventsAndAlertsComponent
										gridProps={{
											sx: {
												bgcolor: "white",
												minWidth: 200,
												minHeight: 364,
												height: 364,
											},
										}}
									/>
								</Box>
							</Grid>
							<Grid
								item
								xs={12}
								md={2.5}
								container
								spacing={1.5}
								direction="column"
							>
								{eventsSection.cards
									.filter((c: Card) => userHasAccessToCode(c.action))
									.slice(1, 3)
									.map((card: Card, idx: number) => (
										<Grid item key={card.cardId}>
											{LoaderContainer({
												height: 176,
												children: (
													<Box sx={{ height: 176 }}>
														<QlikEmbed
															exposeRefApi={idx === 0 ? setQlikApi : undefined}
															ui="analytics/chart"
															appId={APP_ID}
															objectId={card.cardId}
															identity={userInfo?.id}
															style={{ width: "100%", height: "100%" }}
														/>
													</Box>
												),
											})}
										</Grid>
									))}
							</Grid>
							<Grid
								item
								xs={12}
								md={2.5}
								container
								spacing={2}
								direction="column"
							>
								{eventsSection.cards
									.filter((c: Card) => userHasAccessToCode(c.action))
									.slice(3)
									.map((card: Card) => (
										<Grid item key={card.cardId}>
											{LoaderContainer({
												height: 364,
												children: (
													<Box sx={{ height: 364 }}>
														<QlikEmbed
															ui="analytics/chart"
															appId={APP_ID}
															objectId={card.cardId}
															identity={userInfo?.id}
															style={{ width: "100%", height: "100%" }}
														/>
													</Box>
												),
											})}
										</Grid>
									))}
							</Grid>
						</Grid>
					)}
					{/* Multi-Treatment KPIs Section - Grouped by Event */}
					{eventsWithKPIs.length > 0 && (
						<>
							{eventsWithKPIs.map((eventGroup) =>
								eventGroup ? (
									<Box key={eventGroup.eventId} sx={{ mb: 4 }}>
										{/* Section Title */}
										<Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
											{eventGroup.sectionTitle}
										</Typography>

										{/* Event KPIs Row */}
										<Grid container spacing={2}>
											{eventGroup.kpis.map((kpi: KPI) => {
												// Create event-specific identity to isolate variables
												const eventSpecificIdentity = `${userInfo?.id}-event-${eventGroup.eventId}-${kpi.variables.map((v) => v.value).join("-")}`;

												// Handler to store API and apply initial filters
												const handleKpiApiReady = (api: any) => {
													if (!api) return;

													// Store API reference with variables for reapplication
													kpiApisRef.current.set(kpi._id, {
														api,
														variables: kpi.variables,
													});

													// Apply initial filters
													api.getDoc().then(({ app }: any) => {
														app.clearAll();

														// Set date range
														const now = new Date();
														const start = new Date(now);
														const daysBack =
															filters.dateRange === "lastYear"
																? 365
																: filters.dateRange === "lastQuarter"
																	? 90
																	: 30;
														start.setDate(now.getDate() - daysBack);

														const startDate = start.toISOString().slice(0, 10);
														const today = now.toISOString().slice(0, 10);

														app
															.getVariableByName("vStartDate")
															.then((v: any) => v.setStringValue(startDate))
															.catch((e: Error) => {
																console.log(e);
															});

														app
															.getVariableByName("vEndDate")
															.then((v: any) => v.setStringValue(today))
															.catch((e: Error) => {
																console.log(e);
															});

														// Set program filter
														if (filters.programId) {
															app
																.getVariableByName("vSelectedProgram")
																.then((v: any) =>
																	v.setStringValue(filters.programId),
																)
																.catch((e: Error) => {
																	console.log(e);
																});
														}

														// Set provider site filter
														if (filters.providerSiteId) {
															app
																.getVariableByName("vSelectedOU")
																.then((v: any) =>
																	v.setStringValue(
																		filters.providerSiteId.replaceAll(",", "|"),
																	),
																)
																.catch((e: Error) => {
																	console.log(e);
																});
														}

														// Set provider filter
														if (filters.providerId) {
															app
																.getVariableByName("vSelectedProvider")
																.then((v: any) =>
																	v.setStringValue(filters.providerId),
																)
																.catch((e: Error) => {
																	console.log(e);
																});
														}

														// Apply zip code filter if present
														if (filters.zipCode) {
															app
																.getField("organizations.address_zipCode")
																.then((f: any) =>
																	f.selectValues(
																		[{ qText: filters.zipCode }],
																		false,
																		false,
																	),
																)
																.catch((e: Error) => {
																	console.log(e);
																});
														}

														// Set KPI-specific variables
														if (kpi.variables.length > 0) {
															kpi.variables.forEach((variable) => {
																app
																	.getVariableByName(variable.name)
																	.then((v: any) =>
																		v.setStringValue(variable.value),
																	)
																	.catch((error: any) => {
																		console.log(
																			`Variable ${variable.name} not found for KPI ${kpi.name}:`,
																			error,
																		);
																	});
															});
														}
													});
												};

												return (
													<Grid item key={kpi._id} xs={12} sm={6} md={4} lg={3}>
														{LoaderContainer({
															height: 200,
															children: (
																<Box sx={{ height: 200 }}>
																	<QlikEmbed
																		ui="analytics/chart"
																		appId={APP_ID}
																		objectId={kpi.url}
																		identity={eventSpecificIdentity}
																		exposeRefApi={handleKpiApiReady}
																		style={{ width: "100%", height: "100%" }}
																	/>
																</Box>
															),
														})}
													</Grid>
												);
											})}
										</Grid>
									</Box>
								) : null,
							)}
						</>
					)}{" "}
					{/* Fallback message when no KPIs are available */}
					{eventsWithKPIs.length === 0 && filters.programId && (
						<Box
							sx={{
								padding: 3,
								border: `1px dashed ${theme.palette.divider}`,
								borderRadius: 1,
								textAlign: "center",
								mt: 4,
							}}
						>
							<Typography variant="h6" color="textSecondary">
								No Multi-Treatment KPIs Available
							</Typography>
							<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
								Select a program with treatment plan events that have active
								dashboard KPIs
							</Typography>
						</Box>
					)}
				</>
			)}
		</Container>
	);
};

export default MultiTreatmentDashboard;
